import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './AddTransportPerson.css';
import './SchoolDashboard.css';

const AddReceptionist = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schoolData, setSchoolData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    dateOfBirth: '',
    gender: '',
    nationality: 'Kenyan',
    password: '',
    confirmPassword: '',
    employeeId: ''
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessPopup, setShowSuccessPopup] = useState(false);
  const [successData, setSuccessData] = useState(null);
  const [phoneCountryCode, setPhoneCountryCode] = useState('+254'); // Default: Kenya
  const { selectedBranchId } = useBranchFilter();

  // Branch state
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);
  const [formBranchId, setFormBranchId] = useState(null); // Branch selected in form when "All Branches" is selected

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      setSchoolData(JSON.parse(storedSchoolData));
    }
    setLoading(false);
  }, []);

  // Fetch branches when school data is available
  useEffect(() => {
    const fetchBranches = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingBranches(true);
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('branch_name');
        
        if (error) {
          console.error('Error fetching branches:', error);
          setBranches([]);
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (schoolData?.id) {
      fetchBranches();
    }
  }, [schoolData?.id]);

  const validateForm = () => {
    const newErrors = {};

    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    }

    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    }

    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }

    if (!formData.dateOfBirth) {
      newErrors.dateOfBirth = 'Date of birth is required';
    }

    if (!formData.gender) {
      newErrors.gender = 'Gender is required';
    }

    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    // Validate branch selection when "All Branches" is selected
    if (!selectedBranchId && branches.length > 0 && !formBranchId) {
      newErrors.branchId = 'Branch selection is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Generate password function: word + numbers (minimum 6 characters total)
  const generatePassword = () => {
    const words = ['book', 'star', 'moon', 'tree', 'bird', 'fish', 'rock', 'wind', 'fire', 'wave', 'lake', 'hill', 'rose', 'leaf', 'snow', 'rain', 'sun', 'sky', 'sea', 'gem'];
    const randomWord = words[Math.floor(Math.random() * words.length)];
    const wordLength = randomWord.length;
    
    // Calculate how many digits we need to ensure minimum 6 characters total
    // If word is 3 chars, need 3 digits (total 6)
    // If word is 4 chars, need 2 digits (total 6)
    // If word is 5+ chars, need 1 digit (total 6+)
    const minDigitsNeeded = Math.max(1, 6 - wordLength);
    
    // Generate random number with appropriate number of digits
    // For 1 digit: 0-9
    // For 2 digits: 10-99
    // For 3 digits: 100-999
    const maxNumber = Math.pow(10, minDigitsNeeded) - 1;
    const minNumber = minDigitsNeeded === 1 ? 0 : Math.pow(10, minDigitsNeeded - 1);
    const randomNumber = Math.floor(Math.random() * (maxNumber - minNumber + 1)) + minNumber;
    const randomDigits = randomNumber.toString().padStart(minDigitsNeeded, '0');
    
    return randomWord + randomDigits;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      // First, create user in Supabase Auth
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email,
        password: formData.password,
        options: {
          data: {
            name: `${formData.firstName} ${formData.lastName}`,
            user_type: 'receptionist',
            school_id: schoolData.id
          }
        }
      });

      if (authError) {
        console.error('Auth error:', authError);
        // Email strictness removed - if email already exists in auth, continue without blocking
        if (authError.message && authError.message.includes('already registered')) {
          console.log('Email already registered in auth.users (may be used for another user type), continuing...');
          // Continue without blocking - the receptionist record will be created without auth_user_id
        } else {
          alert(`Failed to create receptionist account: ${authError.message}`);
          setIsSubmitting(false);
          return;
        }
      }

      if (authData.user) {
        console.log('Auth user created:', authData.user);
        
        // Wait a moment for the auth user to be fully created
        await new Promise(resolve => setTimeout(resolve, 1000));
        
        // Create user record in users table
        try {
          console.log('Creating user record in users table');
          const { data: userRecord, error: userError } = await supabase
            .from('users')
            .insert({
              id: authData.user.id,
              email: formData.email,
              first_name: formData.firstName.trim(),
              last_name: formData.lastName.trim(),
              user_type: 'receptionist'
            })
            .select()
            .single();
          
          if (userError) {
            console.warn('Could not create receptionist user record:', userError);
            // Continue anyway - auth user exists
          } else {
            console.log('Receptionist user record created successfully:', userRecord);
          }
        } catch (userErr) {
          console.warn('Receptionist user record creation failed:', userErr);
          // Continue anyway - auth user exists
        }
        
        // Use formBranchId if available, otherwise fallback to selectedBranchId
        const finalBranchId = formBranchId || selectedBranchId || null;
        
        // Insert receptionist details into receptionists table
        const { data: insertData, error: dbError } = await supabase
          .from('receptionists')
          .insert({
            id: authData.user.id, // Use the auth user ID
            school_id: schoolData.id,
            branch_id: finalBranchId,
            name: `${formData.firstName.trim()} ${formData.lastName.trim()}`,
            first_name: formData.firstName.trim(),
            last_name: formData.lastName.trim(),
            email: formData.email.trim(),
            phone: phoneCountryCode && formData.phone.trim() ? `${phoneCountryCode} ${formData.phone.trim()}` : (formData.phone.trim() || null),
            address: formData.address.trim() || null,
            date_of_birth: formData.dateOfBirth || null,
            gender: formData.gender || null,
            nationality: formData.nationality || 'Kenyan',
            employee_id: formData.employeeId.trim() || null,
            is_active: true,
            created_at: new Date().toISOString(),
            updated_at: new Date().toISOString()
          })
          .select();

        if (dbError) {
          console.error('Database error:', dbError);
          alert(`Failed to save receptionist details: ${dbError.message}`);
          setIsSubmitting(false);
          return;
        }

        console.log('Receptionist created:', insertData);
        
        // Store success data for popup
        setSuccessData({
          name: `${formData.firstName} ${formData.lastName}`,
          email: formData.email,
          password: formData.password,
          employeeId: formData.employeeId || 'N/A'
        });
        
        // Show success popup
        setShowSuccessPopup(true);
        
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          email: '',
          phone: '',
          address: '',
          dateOfBirth: '',
          gender: '',
          nationality: 'Kenyan',
          password: '',
          confirmPassword: '',
          employeeId: ''
        });
        setPhoneCountryCode('+254'); // Reset to Kenya default
        setFormBranchId(null); // Reset branch selection
        setErrors({});
        setShowPassword(false);
        setShowConfirmPassword(false);
      }
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to add receptionist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="school-dashboard">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="school-dashboard">
        <div className="error-container">
          <h2>Access Denied</h2>
          <p>Please log in to access the school dashboard.</p>
          <button onClick={() => navigate('/schoollogin')} className="btn-primary">
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />
      
      <div className="main-content">
        <div className="content-header">
          <h1>📞 Add Receptionist</h1>
          <p>Add a new receptionist to your school system</p>
        </div>

        <div className="add-transport-person-container">
          <div className="section-card">
            <h2>📝 Receptionist Details</h2>
            <p className="section-description">
              Fill in the details below to add a new receptionist. They will receive login credentials.
            </p>
            
            <form onSubmit={handleSubmit} className="transport-person-form">
              {/* Branch Selection */}
              {!selectedBranchId && branches.length > 0 && (
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="formBranchId">Branch *</label>
                    <select
                      id="formBranchId"
                      name="formBranchId"
                      value={formBranchId || ''}
                      onChange={(e) => setFormBranchId(e.target.value ? parseInt(e.target.value) : null)}
                      className={`form-input ${errors.branchId ? 'error' : ''}`}
                    >
                      <option value="">Select Branch</option>
                      {branches.map((branch) => (
                        <option key={branch.id} value={branch.id}>
                          {branch.branch_name}
                        </option>
                      ))}
                    </select>
                    {errors.branchId && <span className="error-message">{errors.branchId}</span>}
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                      Receptionists cannot be added to "All Branches". Please select a specific branch.
                    </small>
                  </div>
                </div>
              )}
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="firstName">First Name *</label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.firstName ? 'error' : ''}`}
                    placeholder="Enter first name"
                    required
                  />
                  {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="lastName">Last Name *</label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className={`form-input ${errors.lastName ? 'error' : ''}`}
                    placeholder="Enter last name"
                    required
                  />
                  {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="employeeId">Employee ID</label>
                  <input
                    type="text"
                    id="employeeId"
                    name="employeeId"
                    value={formData.employeeId}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter employee ID (optional)"
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="email">Email Address *</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className={`form-input ${errors.email ? 'error' : ''}`}
                    placeholder="Enter email address"
                    required
                  />
                  {errors.email && <span className="error-message">{errors.email}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="phone">Phone Number *</label>
                  <div className="phone-input-wrapper" style={{ display: 'flex', gap: '8px' }}>
                    <select
                      className="country-code-select"
                      value={phoneCountryCode}
                      onChange={(e) => setPhoneCountryCode(e.target.value)}
                      style={{ 
                        width: '50%', 
                        minWidth: '150px', 
                        padding: '0.75rem', 
                        border: '1px solid #D1D5DB', 
                        borderRadius: '6px', 
                        fontSize: '0.95rem', 
                        flexShrink: 0,
                        backgroundColor: '#FFFFFF'
                      }}
                    >
                      <option value="+1">🇺🇸 +1 (US/CA)</option>
                      <option value="+44">🇬🇧 +44 (UK)</option>
                      <option value="+91">🇮🇳 +91 (India)</option>
                      <option value="+92">🇵🇰 +92 (Pakistan)</option>
                      <option value="+254">🇰🇪 +254 (Kenya)</option>
                      <option value="+971">🇦🇪 +971 (UAE)</option>
                      <option value="+966">🇸🇦 +966 (Saudi Arabia)</option>
                      <option value="+974">🇶🇦 +974 (Qatar)</option>
                      <option value="+965">🇰🇼 +965 (Kuwait)</option>
                      <option value="+973">🇧🇭 +973 (Bahrain)</option>
                      <option value="+968">🇴🇲 +968 (Oman)</option>
                      <option value="+961">🇱🇧 +961 (Lebanon)</option>
                      <option value="+962">🇯🇴 +962 (Jordan)</option>
                      <option value="+20">🇪🇬 +20 (Egypt)</option>
                      <option value="+27">🇿🇦 +27 (South Africa)</option>
                      <option value="+234">🇳🇬 +234 (Nigeria)</option>
                      <option value="+233">🇬🇭 +233 (Ghana)</option>
                      <option value="+255">🇹🇿 +255 (Tanzania)</option>
                      <option value="+256">🇺🇬 +256 (Uganda)</option>
                      <option value="+250">🇷🇼 +250 (Rwanda)</option>
                    </select>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`form-input ${errors.phone ? 'error' : ''}`}
                      placeholder="Enter phone number"
                      required
                      style={{ flex: 1 }}
                    />
                  </div>
                  {errors.phone && <span className="error-message">{errors.phone}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="dateOfBirth">Date of Birth *</label>
                  <input
                    type="date"
                    id="dateOfBirth"
                    name="dateOfBirth"
                    value={formData.dateOfBirth}
                    onChange={handleInputChange}
                    className={`form-input ${errors.dateOfBirth ? 'error' : ''}`}
                    required
                  />
                  {errors.dateOfBirth && <span className="error-message">{errors.dateOfBirth}</span>}
                </div>
                
                <div className="form-group">
                  <label htmlFor="gender">Gender *</label>
                  <select
                    id="gender"
                    name="gender"
                    value={formData.gender}
                    onChange={handleInputChange}
                    className={`form-input ${errors.gender ? 'error' : ''}`}
                    required
                  >
                    <option value="">-- Select Gender --</option>
                    <option value="Male">Male</option>
                    <option value="Female">Female</option>
                    <option value="Other">Other</option>
                  </select>
                  {errors.gender && <span className="error-message">{errors.gender}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="nationality">Nationality</label>
                  <input
                    type="text"
                    id="nationality"
                    name="nationality"
                    value={formData.nationality}
                    onChange={handleInputChange}
                    className="form-input"
                    placeholder="Enter nationality"
                  />
                </div>
                
                <div className="form-group" style={{ gridColumn: '1 / -1' }}>
                  <label htmlFor="address">Address *</label>
                  <textarea
                    id="address"
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    className={`form-input ${errors.address ? 'error' : ''}`}
                    placeholder="Enter full address"
                    rows="3"
                    required
                  />
                  {errors.address && <span className="error-message">{errors.address}</span>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="password">Password *</label>
                  <div className="password-input-wrapper" style={{ position: 'relative' }}>
                    <input
                      type={showPassword ? "text" : "password"}
                      id="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className={`form-input ${errors.password ? 'error' : ''}`}
                      placeholder="Enter password"
                      required
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <span 
                      className="password-toggle-icon"
                      onClick={() => setShowPassword(!showPassword)}
                      title={showPassword ? "Hide password" : "Show password"}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        userSelect: 'none',
                        zIndex: 1
                      }}
                    >
                      {showPassword ? '🙈' : '👁️'}
                    </span>
                  </div>
                  {errors.password && <span className="error-message">{errors.password}</span>}
                  <div style={{ marginTop: '0.5rem' }}>
                    <button
                      type="button"
                      className="toggle-btn"
                      onClick={() => {
                        const genPassword = generatePassword();
                        setFormData(prev => ({
                          ...prev,
                          password: genPassword,
                          confirmPassword: genPassword
                        }));
                      }}
                      style={{
                        padding: '0.4rem 0.8rem',
                        backgroundColor: '#10B981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontWeight: '500',
                        fontSize: '0.85rem'
                      }}
                    >
                      Generate Password (min 6 characters)
                    </button>
                    <small style={{ display: 'block', marginTop: '0.25rem', color: '#6B7280', fontSize: '0.75rem' }}>
                      Remember: Password must be at least 6 characters
                    </small>
                  </div>
                </div>
                
                <div className="form-group">
                  <label htmlFor="confirmPassword">Confirm Password *</label>
                  <div className="password-input-wrapper" style={{ position: 'relative' }}>
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      id="confirmPassword"
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleInputChange}
                      className={`form-input ${errors.confirmPassword ? 'error' : ''}`}
                      placeholder="Confirm password"
                      required
                      style={{ paddingRight: '2.5rem' }}
                    />
                    <span 
                      className="password-toggle-icon"
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      title={showConfirmPassword ? "Hide password" : "Show password"}
                      style={{
                        position: 'absolute',
                        right: '0.75rem',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        cursor: 'pointer',
                        fontSize: '1.2rem',
                        userSelect: 'none',
                        zIndex: 1
                      }}
                    >
                      {showConfirmPassword ? '🙈' : '👁️'}
                    </span>
                  </div>
                  {errors.confirmPassword && <span className="error-message">{errors.confirmPassword}</span>}
                </div>
              </div>

              <div className="form-actions">
                <button 
                  type="submit" 
                  className="btn-primary"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '⏳ Adding...' : '📞 Add Receptionist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Success Popup */}
      {showSuccessPopup && successData && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => {
            setShowSuccessPopup(false);
            setSuccessData(null);
          }}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Receptionist Added Successfully! 🎉
            </h2>

            {/* Subtitle */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              {successData.name} has been added to the system
            </p>

            {/* Credentials Card */}
            <div style={{
              background: '#F9FAFB',
              borderRadius: '12px',
              padding: '1.5rem',
              marginBottom: '1.5rem',
              border: '2px solid #E5E7EB',
              position: 'relative',
              zIndex: 1
            }}>
              <h3 style={{
                color: '#374151',
                fontSize: '1rem',
                fontWeight: '600',
                marginBottom: '1rem',
                textAlign: 'center'
              }}>
                Login Credentials
              </h3>
              
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                {/* Employee ID */}
                {successData.employeeId && successData.employeeId !== 'N/A' && (
                  <div style={{
                    background: '#FFFFFF',
                    borderRadius: '8px',
                    padding: '1rem',
                    border: '1px solid #E5E7EB'
                  }}>
                    <div style={{
                      fontSize: '0.75rem',
                      color: '#6B7280',
                      fontWeight: '500',
                      marginBottom: '0.5rem',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px'
                    }}>
                      Employee ID
                    </div>
                    <div style={{
                      fontSize: '1rem',
                      color: '#1F2937',
                      fontWeight: '600',
                      wordBreak: 'break-all'
                    }}>
                      {successData.employeeId}
                    </div>
                  </div>
                )}

                {/* Email */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Email
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    wordBreak: 'break-all'
                  }}>
                    {successData.email}
                  </div>
                </div>

                {/* Password */}
                <div style={{
                  background: '#FFFFFF',
                  borderRadius: '8px',
                  padding: '1rem',
                  border: '1px solid #E5E7EB'
                }}>
                  <div style={{
                    fontSize: '0.75rem',
                    color: '#6B7280',
                    fontWeight: '500',
                    marginBottom: '0.5rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px'
                  }}>
                    Password
                  </div>
                  <div style={{
                    fontSize: '1rem',
                    color: '#1F2937',
                    fontWeight: '600',
                    fontFamily: 'monospace',
                    letterSpacing: '1px'
                  }}>
                    {successData.password}
                  </div>
                </div>
              </div>
            </div>

            {/* Info Message */}
            <div style={{
              background: '#EFF6FF',
              borderRadius: '8px',
              padding: '1rem',
              marginBottom: '1.5rem',
              border: '1px solid #BFDBFE',
              position: 'relative',
              zIndex: 1
            }}>
              <p style={{
                color: '#1E40AF',
                fontSize: '0.875rem',
                margin: 0,
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '0.5rem'
              }}>
                <span>ℹ️</span>
                <span>Please share these credentials with the receptionist</span>
              </p>
            </div>

            {/* Close Button */}
            <button
              onClick={() => {
                setShowSuccessPopup(false);
                setSuccessData(null);
              }}
              style={{
                width: '100%',
                padding: '0.875rem 1.5rem',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)',
                position: 'relative',
                zIndex: 1
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              Done
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default AddReceptionist;

